import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BlockBlobClient } from '@azure/storage-blob';
import { catchError, firstValueFrom, forkJoin, from, Observable, of, switchMap, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { UserService } from '../../security/users/services/user.service';
import { Attachment } from '../models/attachment.model';
import { v4 as uuidv4 } from 'uuid';

@Injectable({
  providedIn: 'root'
})
export class AttachmentService {

  filesEndpoint = `${environment.apiUrl}/Blobs`;

  constructor(private http: HttpClient, private _userService: UserService) { }

  updateAttachmentMetadata(attachments: Attachment[], payload: any): Observable<any> {
    const updatePromises = attachments.map(attachment => {
      return this.http.patch(`${this.filesEndpoint}(${attachment.metadataId})`, payload);
    });

    return forkJoin(updatePromises).pipe(
      tap(results => console.log('All attachments updated successfully:', results)),
      catchError(error => {
        console.error('Error updating attachments:', error);
        throw error;
      })
    );
  }

  async removeUploadingFile(url: string) {
    const blobClient = new BlockBlobClient(url);
    await blobClient.delete({ deleteSnapshots: 'include' });
    console.log(`File with URL ${url} deleted successfully.`);
  }

  removeUploadedFile(fileName: string) {
    return this.getFileDeleteToken(fileName)
      .pipe(
        switchMap(uploadInfo => this.removeUploadingFile(uploadInfo.deleteSasUri)),
        catchError(error => {
          console.error(`Error removing file ${fileName}:`, error);
          return of(error);
        })
      )
  }

  duplicateFile(blobName: string) {
    return this.getFileReadTokenUrl(blobName)
      .pipe(
        switchMap(async uploadInfo => {
          const sourceBlobClient = new BlockBlobClient(uploadInfo.readSasUri);
          const newBlobUrl = await firstValueFrom(this.getFileUploadToken(this._userService.currentUser.id,
            `${uuidv4()}-copy.${blobName.split('.').pop() || ''}`));
          const destinationBlobClient = new BlockBlobClient(newBlobUrl.uploadUrl);
          const copyPoller = await destinationBlobClient.beginCopyFromURL(sourceBlobClient.url);
          const copyResult = await copyPoller.pollUntilDone();
          if (!copyResult.copyId) {
            throw new Error('File duplication failed');
          }
          console.log(`File ${blobName} duplicated successfully to ${newBlobUrl.blobName}`);
          return {
            blobName: newBlobUrl.blobName,
            readSasUri: uploadInfo.readSasUri,
            expiresOn: uploadInfo.expiresOn
          };
        }),
        tap(() => {
          console.log(`File with blob name ${blobName} duplicated successfully.`);
        }),
        catchError(error => {
          console.error(`Error duplicating file ${blobName}:`, error);
          return of(error);
        })
      );
  }

  async duplicateBlobFile(blobName: string): Promise<{ blobName: string, readSasUri: string, expiresOn: string, metadataId: number }> {
    try {
      const uploadInfo = await firstValueFrom(this.getFileReadTokenUrl(blobName));

      if (!uploadInfo.readSasUri) {
        throw new Error(`Failed to get read SAS URI for blob: ${blobName}`);
      }

      const sourceBlobClient = new BlockBlobClient(uploadInfo.readSasUri);
      const fileExtension = blobName.split('.').pop() || '';
      const newFileName = `${uuidv4()}-copy.${fileExtension}`;

      const newBlobUrl = await firstValueFrom(
        this.getFileUploadToken(this._userService.currentUser.id, newFileName)
      );

      const destinationBlobClient = new BlockBlobClient(newBlobUrl.uploadUrl);
      const copyPoller = await destinationBlobClient.beginCopyFromURL(sourceBlobClient.url);
      const copyResult = await copyPoller.pollUntilDone();

      if (!copyResult.copyId) {
        throw new Error('File duplication failed');
      }

      const saveResult: any = await firstValueFrom(
        this.save({
          uploadUrl: newBlobUrl.uploadUrl,
          blobName: newBlobUrl.blobName
        }, new File([], newFileName, { type: fileExtension }))
      );

      console.log(`File ${blobName} duplicated successfully to ${newBlobUrl.blobName}`);

      return {
        blobName: newBlobUrl.blobName,
        readSasUri: uploadInfo.readSasUri,
        expiresOn: uploadInfo.expiresOn,
        metadataId: saveResult.Id
      };
    } catch (error) {
      console.error(`Error duplicating file ${blobName}:`, error);
      throw error; // Re-throw the error instead of returning it
    }
  }

  async duplicateBlobFiles(blobNames: string[]): Promise<{ blobName: string, readSasUri: string, expiresOn: string, metadataId: number }[]> {
    const results: { blobName: string, readSasUri: string, expiresOn: string, metadataId: number }[] = [];

    for (const blobName of blobNames) {
      try {
        const result = await this.duplicateBlobFile(blobName);
        results.push(result);
        console.log(`Successfully duplicated: ${blobName}`);
      } catch (error) {
        console.error(`Failed to duplicate ${blobName}:`, error);
      }
    }

    return results;
  }

  getFileDeleteToken(blobName: string) {
    return this.http
      .get<{ blobName: String, deleteSasUri: string, expiresOn: string }>(
        `${this.filesEndpoint}/delete-sas`,
        { params: { blobName } }
      );
  }

  getFileUploadToken(userId: string, fileName: string) {
    return this.http
      .get<{ uploadUrl: string; blobName: string }>(
        `${this.filesEndpoint}/write-delete-sas`,
        { params: { userId, fileName } }
      );
  }

  getFileReadTokenUrl(blobName: string) {
    return this.http
      .get<{ readSasUri: string; blobName: string, expiresOn: string }>(
        `${this.filesEndpoint}/read-sas`,
        { params: { blobName } }
      );
  }

  getAttachmentReadUrl(blobName: string, expiresOn: string, readSasUri: string) {
    console.log(Date.now() - Date.parse(expiresOn));

    if (!expiresOn || !readSasUri || Date.parse(expiresOn) < Date.now()) {
      return this.http
        .get<{ readSasUri: string; blobName: string, expiresOn: string }>(
          `${this.filesEndpoint}/read-sas`,
          { params: { blobName } }
        );
    }
    return of({ readSasUri, blobName, expiresOn });
  }

  shortFileNameWithExt(fileName: string): string {
    const maxLength = 20;
    const ext = fileName.split('.').pop();
    const nameWithoutExt = fileName.substring(0, fileName.length - ext.length - 1);

    if (nameWithoutExt.length > maxLength) {
      return nameWithoutExt.substring(0, maxLength - 3) + '...' + '.' + ext;
    }
    return fileName;
  }

  save(uploadInfo: { uploadUrl: string; blobName: string; }, file: File) {
    return this.http.post(this.filesEndpoint, {
      FileUrl: uploadInfo.uploadUrl.split('?')[0],
      FileName: uploadInfo.blobName,
      OriginalFileName: file.name,
      FileType: file.type,
      FileSize: file.size,
      OwnerId: this._userService.currentUser.id
    })
  }

  deleteFileMetadata(metadataId: number): Observable<any> {
    return this.http.delete(`${this.filesEndpoint}(${metadataId})`);
  }

  saveMultipleForEntity(attachments: Attachment[], entityPartialPayload: any): Observable<any> {
    const updatePromises = attachments.map(attachment => {
      const payload = {
        FileName: attachment.FileName,
        // TODO: udate these properties to match the actual attachment properties
        FileUrl: attachment.FileName,
        FileType: attachment.FileName.split('.').pop(),
        FileSize: 10,
        // FileUrl: attachment.FileUrl,
        // FileType: attachment.FileType,
        // FileSize: attachment.FileSize,
        // end TODO
        OwnerId: this._userService.currentUser.id,
        ...entityPartialPayload
      };
      return this.http.post(this.filesEndpoint, payload);
    });

    return forkJoin(updatePromises).pipe(
      tap(results => console.log('All attachments updated successfully:', results)),
      catchError(error => {
        console.error('Error updating attachments:', error);
        throw error;
      })
    );
  }

  sanitizeAndShortenFileName(fileName: string): string {
    const sanitizedFileName = fileName.replace(/[^a-zA-Z0-9._-]/g, '_');
    const shortenedFileName = sanitizedFileName.replace(/_{2,}/g, '_');
    return shortenedFileName.length > 50 ? shortenedFileName.substring(0, 50) : shortenedFileName;
  }
}